import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  HairlineCard,
  Icons,
  iconSize,
  initialsOf,
  Link,
  palette,
  Stack,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@munifocus/ui';
import type { OfficerPositionView } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { useOptionalScope } from '../providers/ScopeProvider';
import { useRealtime } from '../lib/realtime';
import { useLiveOfficers } from '../lib/tracking-api';
import { isStale, sortForList } from '../lib/team-map';
import { timeAgo } from '../lib/relative-time';
import { getTileConfig, NB_BOUNDS } from '../lib/map-tiles';
import { OpenOnScope } from '../components/map/OpenOnScope';
import { frenchFirstOfMonth } from '@munifocus/utils';

function officerIcon(name: string, stale: boolean): L.DivIcon {
  const fill = stale ? palette.semantic.closed : palette.brand.accent;
  const svg = `
    <svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="17" cy="17" r="15" fill="${fill}" stroke="${palette.background.paper}" stroke-width="2"/>
      <text x="17" y="21.5" text-anchor="middle" font-family="Manrope, sans-serif" font-size="12" font-weight="700" fill="${palette.background.paper}">${(initialsOf(name) || '?').replace(/[<>&"]/g, '')}</text>
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'officer-pin',
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -16],
  });
}

function FitOnce({ points }: { points: Array<[number, number]> }) {
  const map = useMap();
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (done || points.length === 0) return;
    if (points.length === 1) map.setView(points[0], 14);
    else map.fitBounds(L.latLngBounds(points), { padding: [48, 48] });
    setDone(true);
  }, [map, points, done]);
  return null;
}

/** "ETA 14:32 (estimated)" for an officer en route, or nothing. */
function useEtaLabel() {
  const { t, i18n } = useTranslation();
  return (o: OfficerPositionView): string | null => {
    if (o.visit?.status !== 'en_route' || !o.visit.plannedEta) return null;
    const time = frenchFirstOfMonth(
      new Intl.DateTimeFormat(i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(o.visit.plannedEta)),
    );
    return o.visit.routeEstimated
      ? t('gov.team.etaEstimated', { time })
      : t('gov.team.eta', { time });
  };
}

/**
 * The supervisor's live team map: every officer on duty at the active
 * municipality, at their latest reported position.
 *
 * Positions arrive over the socket (joined only after the server checks
 * `tracking:view`) and the list re-polls every minute as a safety net. A
 * marker older than five minutes is greyed — the officer may be indoors or out
 * of signal, and the map must not pretend it knows where they are now.
 *
 * The table under the map carries the same data for keyboard and screen-reader
 * users; it is not a summary of the map, it is the map in rows.
 */
export function TeamMap() {
  const { t, i18n } = useTranslation();
  const scope = useOptionalScope();
  const lg = scope?.active?.kind === 'local_government' ? scope.active : null;
  const { data, isLoading, isError, refetch } = useLiveOfficers(lg?.id);
  const realtime = useRealtime();
  const etaLabel = useEtaLabel();

  // Held in a ref: the context value changes identity with connection and
  // typing state, and re-joining the room on every keystroke elsewhere would be
  // churn for nothing. The room depends only on the municipality.
  const realtimeRef = useRef(realtime);
  realtimeRef.current = realtime;
  const lgId = lg?.id;
  useEffect(() => {
    if (!lgId) return;
    return realtimeRef.current.watchTracking(lgId);
  }, [lgId]);

  // Re-render every 15 s so "2 min ago" and the grey-out stay true without a push.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);

  const officers = useMemo(() => sortForList(data ?? [], now), [data, now]);
  const points = useMemo(() => officers.map((o) => [o.lat, o.lng] as [number, number]), [officers]);
  const tiles = getTileConfig();
  const ago = (o: OfficerPositionView) =>
    timeAgo(o.recordedAt, i18n.language, t('gov.team.justNow'), now);

  return (
    <AppShell>
      <Stack
        direction="row"
        sx={{ mb: 2, alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ letterSpacing: '-0.01em' }}>
            {t('gov.team.title')}
          </Typography>
          <Typography sx={{ mt: 0.5, color: palette.text.secondary }}>
            {t('gov.team.subtitle')}
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/team/history"
          variant="outlined"
          startIcon={<Icons.History size={iconSize.md} aria-hidden />}
        >
          {t('gov.team.history.open')}
        </Button>
      </Stack>

      {!lg && <Alert severity="info">{t('gov.team.pickMunicipality')}</Alert>}

      {lg && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}

      {lg && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {t('common.actions.retry')}
            </Button>
          }
        >
          {t('gov.team.loadError')}
        </Alert>
      )}

      {lg && data && (
        <Stack spacing={2}>
          <HairlineCard sx={{ overflow: 'hidden', p: 0 }}>
            <Box
              sx={{
                height: { xs: 360, md: 'calc(100vh - 420px)' },
                minHeight: 360,
                '& .leaflet-container': {
                  height: '100%',
                  width: '100%',
                  background: palette.background.parch,
                },
              }}
            >
              <MapContainer
                bounds={NB_BOUNDS}
                scrollWheelZoom
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url={tiles.url}
                  attribution={tiles.attribution}
                  maxZoom={tiles.maxZoom}
                />
                <FitOnce points={points} />
                <OpenOnScope hasPoints={points.length > 0} />
                {officers.map((o) => {
                  const stale = isStale(o, now);
                  const eta = etaLabel(o);
                  return (
                    <Fragment key={o.officerId}>
                      {o.visit?.destination && o.visit.status === 'en_route' && (
                        <Polyline
                          positions={[
                            [o.lat, o.lng],
                            [o.visit.destination.lat, o.visit.destination.lng],
                          ]}
                          pathOptions={{
                            color: palette.brand.accent,
                            weight: 2,
                            dashArray: '4 6',
                            opacity: stale ? 0.4 : 0.8,
                          }}
                        />
                      )}
                      <Marker
                        position={[o.lat, o.lng]}
                        icon={officerIcon(o.officerName, stale)}
                        title={o.officerName}
                        alt={o.officerName}
                        opacity={stale ? 0.7 : 1}
                      >
                        <Popup>
                          <Stack spacing={0.5} sx={{ minWidth: 200 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                              {o.officerName}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>
                              {stale
                                ? t('gov.team.staleSince', { ago: ago(o) })
                                : t('gov.team.updated', { ago: ago(o) })}
                            </Typography>
                            {o.visit ? (
                              <>
                                <Typography sx={{ fontSize: 13 }}>
                                  {t(`gov.visits.status.${o.visit.status}`)} ·{' '}
                                  <Link component={RouterLink} to={`/files/${o.visit.fileId}`}>
                                    {o.visit.reference}
                                  </Link>
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>
                                  {o.visit.address}
                                </Typography>
                                {eta && <Typography sx={{ fontSize: 13 }}>{eta}</Typography>}
                              </>
                            ) : (
                              <Typography sx={{ fontSize: 13, color: palette.text.secondary }}>
                                {t('gov.team.noVisit')}
                              </Typography>
                            )}
                          </Stack>
                        </Popup>
                      </Marker>
                    </Fragment>
                  );
                })}
              </MapContainer>
            </Box>
          </HairlineCard>

          <HairlineCard sx={{ p: 0, overflowX: 'auto' }}>
            <Table size="small" aria-labelledby="team-list-title">
              <caption id="team-list-title" style={{ captionSide: 'top', textAlign: 'left' }}>
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <Typography variant="overline">{t('gov.team.listTitle')}</Typography>
                </Box>
              </caption>
              <TableHead>
                <TableRow>
                  <TableCell>{t('gov.team.columns.officer')}</TableCell>
                  <TableCell>{t('gov.team.columns.updated')}</TableCell>
                  <TableCell>{t('gov.team.columns.visit')}</TableCell>
                  <TableCell>{t('gov.team.columns.eta')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {officers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        {t('gov.team.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {officers.map((o) => {
                  const stale = isStale(o, now);
                  return (
                    <TableRow key={o.officerId}>
                      <TableCell>{o.officerName}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <span>{ago(o)}</span>
                          {stale && <StatusChip status="neutral" label={t('gov.team.stale')} />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {o.visit ? (
                          <>
                            {t(`gov.visits.status.${o.visit.status}`)} ·{' '}
                            <Link component={RouterLink} to={`/files/${o.visit.fileId}`}>
                              {o.visit.reference}
                            </Link>
                            , {o.visit.address}
                          </>
                        ) : (
                          t('gov.team.noVisit')
                        )}
                      </TableCell>
                      <TableCell>{etaLabel(o) ?? '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </HairlineCard>

          <Typography variant="caption" color="text.secondary">
            {t('gov.team.privacyNote')}
          </Typography>
        </Stack>
      )}
    </AppShell>
  );
}
